import { useMemo, useState } from 'react';
import { Coffee, Flame, MoonStar, Search, SearchX, Sun, Sunset } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { MenuItemCard } from '@/components/MenuItemCard';
import { useMealPeriod } from '@/hooks/useMealPeriod';
import { CATEGORY_META, MENU, type MealCategory } from '@/lib/menu';
import { cn } from '@/lib/utils';

type TabValue = MealCategory | 'all';

const TABS: Array<{ value: TabValue; label: string; icon: typeof Sun }> = [
  { value: 'all', label: 'All', icon: Flame },
  { value: 'breakfast', label: 'Breakfast', icon: Coffee },
  { value: 'lunch', label: 'Lunch', icon: Sun },
  { value: 'dinner', label: 'Dinner', icon: Sunset },
  { value: 'drinks', label: 'Drinks', icon: MoonStar },
];

export function MenuSection() {
  const info = useMealPeriod();
  const [tab, setTab] = useState<TabValue>(info.menuCategory);
  const [query, setQuery] = useState('');

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MENU.filter((item) => {
      const inTab = tab === 'all' || item.category === tab;
      const inQuery =
        q.length === 0 ||
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q);
      return inTab && inQuery;
    });
  }, [tab, query]);

  const activeMeta = tab === 'all' ? null : CATEGORY_META[tab];

  return (
    <section id="menu" className="scroll-mt-20 py-16 sm:py-20">
      <div className="container">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-primary">
              <Flame className="size-4" />
              The Menu — Menyu
            </p>
            <h2 className="mt-3 font-display text-4xl font-black tracking-tight sm:text-5xl">
              What are you craving{' '}
              <span className="italic text-primary">leo?</span>
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">
              Every dish is cooked to order on the jiko. Right now we recommend the{' '}
              <span className="font-semibold text-foreground">
                {CATEGORY_META[info.menuCategory].label.toLowerCase()}
              </span>{' '}
              menu — but the whole kitchen is open for you.
            </p>
          </div>

          <div className="relative w-full max-w-sm shrink-0">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search nyama choma, chai, pilau…"
              className="h-12 rounded-full bg-card pl-10 shadow-sm"
              aria-label="Search the menu"
            />
          </div>
        </div>

        {/* Category tabs */}
        <div
          className="scrollbar-hide -mx-4 mt-8 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0"
          role="tablist"
          aria-label="Menu categories"
        >
          {TABS.map(({ value, label, icon: Icon }) => {
            const isActive = tab === value;
            const isNow = value === info.menuCategory;
            const count = value === 'all' ? MENU.length : MENU.filter((m) => m.category === value).length;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setTab(value)}
                className={cn(
                  'flex shrink-0 items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground shadow-md shadow-primary/25'
                    : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground',
                )}
              >
                <Icon className="size-4" />
                {label}
                {isNow && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide',
                      isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-ember/15 text-primary',
                    )}
                  >
                    Now
                  </span>
                )}
                <span className={cn('text-xs font-semibold', isActive ? 'opacity-80' : 'opacity-50')}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active category meta */}
        {activeMeta && (
          <p className="mt-5 text-sm font-medium text-muted-foreground">
            <span className="font-display text-base font-bold italic text-foreground">
              {activeMeta.swahili}
            </span>
            {' · '}Served {activeMeta.hours} EAT
          </p>
        )}

        {/* Grid */}
        {items.length > 0 ? (
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {items.map((item) => (
              <MenuItemCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-dashed border-border bg-card/50 py-16 text-center">
            <SearchX className="mx-auto size-10 text-muted-foreground/50" />
            <p className="mt-4 font-display text-xl font-bold">Hakuna matata — but no matches</p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
              We couldn't find "{query}" on the menu. Try another dish or clear the search.
            </p>
            <button
              type="button"
              onClick={() => setQuery('')}
              className="mt-5 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Clear search
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
